/**
 * ReplyToContext
 *
 * Provides a mechanism for setting a "reply to" item that displays above the textarea
 * in ThreadView. Used when users want to reference something in their message
 * (e.g., replying to a specific choice from a Response Artifact).
 *
 * Split into two contexts to avoid unnecessary re-renders:
 * - ReplyToStateContext: Contains the replyTo value (only consumed by ReplyToPreview)
 * - ReplyToActionsContext: Contains setReplyTo/clearReplyTo (stable refs, no re-renders)
 *
 * The context automatically clears when the thread changes.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

export interface ReplyToItem {
  id: string;
  actionLine: string;
  descriptionLine: string;
  extraContext?: string;
  bookable?: boolean;
  price?: number;
  price_currency?: string;
  purchaseSummary?: string;
  purchaseLink?: string;
  delivery?: string;
  deliveryAddress?: string;
  isDeposit?: boolean;
  merchantName?: string;
  /** If true, clearReplyTo() will not remove this item — must use removeReplyTo(id) explicitly. */
  persistent?: boolean;
  /** If true, renders as a centered pill with the gradient glow behind the input (e.g. routine creation, daily briefing). */
  glow?: boolean;
}

interface ReplyToStateContextType {
  replyTo: ReplyToItem[];
}

interface ReplyToActionsContextType {
  addReplyTo: (item: ReplyToItem) => void;
  removeReplyTo: (id: string) => void;
  clearReplyTo: () => void;
  getReplyTo: () => ReplyToItem[];
  registerRemoveCallback: (key: string, fn: (id: string) => void) => void;
  unregisterRemoveCallback: (key: string) => void;
}

const ReplyToStateContext = createContext<ReplyToStateContextType | undefined>(
  undefined,
);

const ReplyToActionsContext = createContext<
  ReplyToActionsContextType | undefined
>(undefined);

interface ReplyToProviderProps {
  children: ReactNode;
}

export const ReplyToProvider: React.FC<ReplyToProviderProps> = ({
  children,
}) => {
  const [replyTo, setReplyToState] = useState<ReplyToItem[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track current replyTo value for getter (no re-renders)
  const replyToRef = useRef<ReplyToItem[]>([]);

  // Track previous thread ID to detect changes
  const prevThreadIdRef = useRef<string | null | undefined>(undefined);

  // Clear replyTo when thread changes, or populate from URL reply params
  useEffect(() => {
    const currentThreadId = searchParams.get('thread_id');
    const isNewThread =
      searchParams.get('new') === '' || searchParams.get('new') === 'true';

    // Determine effective thread identifier
    const effectiveThreadId = currentThreadId || (isNewThread ? 'new' : null);

    // Check for reply-to URL params (supports both single and indexed)
    // Single: reply_action, reply_desc, reply_context
    // Indexed: reply_action_0, reply_desc_0, reply_context_0, reply_action_1, ...
    const replyAction = searchParams.get('reply_action');
    const replyDesc = searchParams.get('reply_desc');
    const hasSingleReplyParams = !!(replyAction && replyDesc);
    const hasIndexedReplyParams = !!(
      searchParams.get('reply_action_0') && searchParams.get('reply_desc_0')
    );
    const hasReplyParams = hasSingleReplyParams || hasIndexedReplyParams;

    // Helper to consume and clean up reply params from URL
    const consumeReplyParams = () => {
      const items: ReplyToItem[] = [];

      if (hasIndexedReplyParams) {
        // Collect indexed items: reply_action_0, reply_desc_0, etc.
        for (let i = 0; ; i++) {
          const action = searchParams.get(`reply_action_${i}`);
          const desc = searchParams.get(`reply_desc_${i}`);
          if (!action || !desc) break;
          const priceStr = searchParams.get(`reply_price_${i}`);
          items.push({
            id: `reply_url_${i}`,
            actionLine: action,
            descriptionLine: desc,
            extraContext: searchParams.get(`reply_context_${i}`) || undefined,
            bookable:
              searchParams.get(`reply_bookable_${i}`) === '1' || undefined,
            price: priceStr != null ? Number(priceStr) : undefined,
            price_currency:
              searchParams.get(`reply_price_currency_${i}`) || undefined,
          });
        }
      } else if (hasSingleReplyParams) {
        const priceStr = searchParams.get('reply_price');
        items.push({
          id: 'reply_url',
          actionLine: replyAction!,
          descriptionLine: replyDesc!,
          extraContext: searchParams.get('reply_context') || undefined,
          bookable: searchParams.get('reply_bookable') === '1' || undefined,
          price: priceStr != null ? Number(priceStr) : undefined,
          price_currency: searchParams.get('reply_price_currency') || undefined,
        });
      }

      replyToRef.current = items;
      setReplyToState(items);

      // Remove all reply params from URL to prevent re-triggering
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('reply_action');
      newParams.delete('reply_desc');
      newParams.delete('reply_context');
      newParams.delete('reply_bookable');
      newParams.delete('reply_price');
      newParams.delete('reply_price_currency');
      for (let i = 0; ; i++) {
        const key = `reply_action_${i}`;
        if (!newParams.has(key)) break;
        newParams.delete(`reply_action_${i}`);
        newParams.delete(`reply_desc_${i}`);
        newParams.delete(`reply_context_${i}`);
        newParams.delete(`reply_bookable_${i}`);
        newParams.delete(`reply_price_${i}`);
        newParams.delete(`reply_price_currency_${i}`);
      }
      setSearchParams(newParams, { replace: true });
    };

    // On first render, store current thread and check for reply params
    if (prevThreadIdRef.current === undefined) {
      prevThreadIdRef.current = effectiveThreadId;
      if (hasReplyParams) {
        consumeReplyParams();
      }
      return;
    }

    // If thread changed, populate from reply params or clear
    if (prevThreadIdRef.current !== effectiveThreadId) {
      if (hasReplyParams) {
        consumeReplyParams();
      } else {
        replyToRef.current = [];
        setReplyToState([]);
      }
      prevThreadIdRef.current = effectiveThreadId;
    } else if (hasReplyParams) {
      // Same thread but reply params present (e.g. "Chat about this" from a
      // project app modal while already viewing the thread) — still consume them
      consumeReplyParams();
    }
  }, [searchParams, setSearchParams]);

  // Stable action functions that won't cause re-renders
  const addReplyTo = useCallback((item: ReplyToItem) => {
    const prev = replyToRef.current;
    const idx = prev.findIndex((i) => i.id === item.id);
    const next =
      idx >= 0 ? prev.map((i, j) => (j === idx ? item : i)) : [...prev, item];
    replyToRef.current = next;
    setReplyToState(next);
  }, []);

  const removeReplyTo = useCallback((id: string) => {
    const next = replyToRef.current.filter((i) => i.id !== id);
    replyToRef.current = next;
    setReplyToState(next);
    // Notify registered components so they can deselect
    removeCallbacksRef.current.forEach((fn) => fn(id));
  }, []);

  const clearReplyTo = useCallback(() => {
    const prev = replyToRef.current;
    const persistent = prev.filter((item) => item.persistent);
    const removed = prev.filter((item) => !item.persistent);
    replyToRef.current = persistent;
    setReplyToState(persistent);
    // Notify for each removed item
    removed.forEach((item) => {
      removeCallbacksRef.current.forEach((fn) => fn(item.id));
    });
  }, []);

  // Getter that returns current value without subscribing to changes
  const getReplyTo = useCallback(() => replyToRef.current, []);

  // Remove callback registry — lets components react to item removal
  const removeCallbacksRef = useRef<Map<string, (id: string) => void>>(
    new Map(),
  );

  const registerRemoveCallback = useCallback(
    (key: string, fn: (id: string) => void) => {
      removeCallbacksRef.current.set(key, fn);
    },
    [],
  );

  const unregisterRemoveCallback = useCallback((key: string) => {
    removeCallbacksRef.current.delete(key);
  }, []);

  // Memoize actions object to keep reference stable
  const actions = useRef({
    addReplyTo,
    removeReplyTo,
    clearReplyTo,
    getReplyTo,
    registerRemoveCallback,
    unregisterRemoveCallback,
  });

  return (
    <ReplyToActionsContext.Provider value={actions.current}>
      <ReplyToStateContext.Provider value={{ replyTo: replyTo }}>
        {children}
      </ReplyToStateContext.Provider>
    </ReplyToActionsContext.Provider>
  );
};

/**
 * Hook to access the current replyTo state.
 * Only use this in components that need to render based on the replyTo value.
 * Using this hook will cause re-renders when replyTo changes.
 */
export const useReplyToState = (): ReplyToStateContextType => {
  const context = useContext(ReplyToStateContext);
  if (context === undefined) {
    throw new Error('useReplyToState must be used within a ReplyToProvider');
  }
  return context;
};

/**
 * Hook to access replyTo actions (addReplyTo, removeReplyTo, clearReplyTo).
 * These are stable refs and won't cause re-renders when replyTo changes.
 * Use this in components that only need to add/remove/clear replyTo.
 */
export const useReplyToActions = (): ReplyToActionsContextType => {
  const context = useContext(ReplyToActionsContext);
  if (context === undefined) {
    throw new Error('useReplyToActions must be used within a ReplyToProvider');
  }
  return context;
};
